import React, { createContext, useContext, useState, useReducer, useEffect, useCallback, useRef } from 'react';

// Create context
const CollectionContext = createContext();

// Initial state
const initialState = {
  products: [],
  filters: [],
  activeFilters: {},
  sortOption: 'Featured',
  loading: false,
  initialLoadComplete: false,
  totalResults: 0,
  currentPage: 1,
  isLastPage: false,
  productsPerPage: 24,
  isSorting: false
};

// Action types
const actionTypes = {
  SET_PRODUCTS: 'SET_PRODUCTS',
  APPEND_PRODUCTS: 'APPEND_PRODUCTS',
  SET_FILTERS: 'SET_FILTERS',
  SET_ACTIVE_FILTERS: 'SET_ACTIVE_FILTERS',
  SET_SORT_OPTION: 'SET_SORT_OPTION',
  SET_LOADING: 'SET_LOADING',
  SET_TOTAL_RESULTS: 'SET_TOTAL_RESULTS',
  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  SET_IS_LAST_PAGE: 'SET_IS_LAST_PAGE',
  SET_PRODUCTS_PER_PAGE: 'SET_PRODUCTS_PER_PAGE',
  SET_INITIAL_LOAD_COMPLETE: 'SET_INITIAL_LOAD_COMPLETE',
  SET_IS_SORTING: 'SET_IS_SORTING'
};

// Reducer function
function collectionReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_PRODUCTS:
      return { ...state, products: action.payload };
    case actionTypes.APPEND_PRODUCTS:
      return { ...state, products: [...state.products, ...action.payload] };
    case actionTypes.SET_FILTERS:
      return { ...state, filters: action.payload };
    case actionTypes.SET_ACTIVE_FILTERS:
      return { ...state, activeFilters: action.payload };
    case actionTypes.SET_SORT_OPTION:
      return { ...state, sortOption: action.payload };
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    case actionTypes.SET_TOTAL_RESULTS:
      return { ...state, totalResults: action.payload };
    case actionTypes.SET_CURRENT_PAGE:
      return { ...state, currentPage: action.payload };
    case actionTypes.SET_IS_LAST_PAGE:
      return { ...state, isLastPage: action.payload };
    case actionTypes.SET_PRODUCTS_PER_PAGE:
      return { ...state, productsPerPage: action.payload };
    case actionTypes.SET_INITIAL_LOAD_COMPLETE:
      return { ...state, initialLoadComplete: action.payload };
    case actionTypes.SET_IS_SORTING:
      return { ...state, isSorting: action.payload };
    default:
      return state;
  }
}

// Helper to get URL parameters
const getUrlParam = (paramName) => {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(paramName);
};

// Helper to update URL with page parameter
const updateUrlWithPage = (page) => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location);
  url.searchParams.set('page', page);
  window.history.replaceState({}, '', url);
};

// Provider component
export const CollectionProvider = ({ children, initialProducts = [], defaultSettings = {} }) => {
  // Get page from URL if it exists
  const urlPage = getUrlParam('page');
  const initialPage = urlPage ? parseInt(urlPage, 10) : 1;

  // Get products per page from settings or use default
  const productsPerPage = defaultSettings.productsPerPage || 24;

  // Initialize reducer before using state in useMemo
  const [state, dispatch] = useReducer(collectionReducer, {
    ...initialState,
    products: initialProducts,
    sortOption: defaultSettings.defaultSortOption || initialState.sortOption,
    currentPage: initialPage,
    productsPerPage: productsPerPage
  });

  // Use ref to track current state for event handlers
  const stateRef = useRef(state);
  stateRef.current = state;

  // Debug: Track when products array changes
  const prevProductsLength = useRef(state.products.length);
  const renderCount = useRef(0);
  renderCount.current += 1;

  useEffect(() => {
    if (state.products.length !== prevProductsLength.current) {
      prevProductsLength.current = state.products.length;
    }
  }, [state.products]);

  // Set products per page on initialization
  useEffect(() => {
    dispatch({
      type: actionTypes.SET_PRODUCTS_PER_PAGE,
      payload: productsPerPage
    });
  }, [productsPerPage]);

  // Initialize with correct page from URL
  useEffect(() => {
    if (urlPage && parseInt(urlPage, 10) > 1) {
      // If URL has a page parameter > 1, we need to load all pages up to this one
      loadAllPagesUpTo(parseInt(urlPage, 10));
    }
  }, []);

  // Event handlers
  useEffect(() => {
    const handleFiltersResults = (event) => {
      if (event.detail.type === 'plp') {
        if (event.detail.filters) {
          dispatch({ type: actionTypes.SET_FILTERS, payload: event.detail.filters });
        }
      }
    };

    const handleProductsResults = (event) => {
      if (event.detail.type === 'plp') {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
        
        // Check if products are the same as current products to avoid unnecessary updates
        const incomingProducts = event.detail.products || [];
        const currentProducts = stateRef.current.products || [];

        // Helper function to compare products by their IDs
        const areProductsSame = (products1, products2) => {
          if (products1.length !== products2.length) return false;
          
          const ids1 = products1.map(p => p.id).sort();
          const ids2 = products2.map(p => p.id).sort();
          
          return ids1.every((id, index) => id === ids2[index]);
        };
        
        // Determine how to handle products based on pagination type and context
        const isPageBasedPagination = event.detail.paginationType === 'pages';
        const isLoadMorePagination = event.detail.paginationType === 'load_more';
        const isSubsequentPage = event.detail.page > 1;
        const isFiltering = event.detail.isFiltering;
        const isSorting = event.detail.isSorting || false;

        if (isLoadMorePagination && isSubsequentPage && !isFiltering) {
          // For load more, we're appending products, so we need to check if the new products are already included
          const currentProductIds = new Set(currentProducts.map(p => p.id));
          const newProducts = incomingProducts.filter(p => !currentProductIds.has(p.id));
          
          if (newProducts.length > 0) {
            dispatch({ type: actionTypes.APPEND_PRODUCTS, payload: newProducts });
            if (!event.detail.skipUrlUpdate) {
              updateUrlWithPage(event.detail.page);
            }
          } else {
            console.log('No new products to append, skipping update');
          }
        } else {
          // For page-based or filtering, check if products OR totalResults are different
          const totalResultsChanged = 'totalResults' in event.detail && event.detail.totalResults !== stateRef.current.totalResults;
          const productsChanged = !areProductsSame(incomingProducts, currentProducts);
          
          // Special case: if totalResults is 0, always update products to empty array
          const shouldForceEmptyUpdate = event.detail.totalResults === 0 && currentProducts.length > 0;
          
          // ALWAYS update products if totalResults is 0, regardless of other conditions
          if (event.detail.totalResults === 0) {
            dispatch({ type: actionTypes.SET_PRODUCTS, payload: [] });
          } else if (productsChanged || totalResultsChanged || shouldForceEmptyUpdate || isSorting) {
            dispatch({ type: actionTypes.SET_PRODUCTS, payload: incomingProducts });
          }
        }

        // Always update totalResults, even if zero
        if ('totalResults' in event.detail) {
          dispatch({ type: actionTypes.SET_TOTAL_RESULTS, payload: event.detail.totalResults });
          const totalPages = Math.ceil((event.detail.totalResults || 0) / stateRef.current.productsPerPage);
          dispatch({
            type: actionTypes.SET_IS_LAST_PAGE,
            payload: event.detail.page >= totalPages
          });
        }
        if (event.detail.page) {
          dispatch({ type: actionTypes.SET_CURRENT_PAGE, payload: event.detail.page });
        }
        
        // Mark initial load as complete after first results (including empty results)
        if (!stateRef.current.initialLoadComplete) {
          dispatch({ type: actionTypes.SET_INITIAL_LOAD_COMPLETE, payload: true });
        }

        // Reset sorting flag after processing results
        if (stateRef.current.isSorting) {
          dispatch({ type: actionTypes.SET_IS_SORTING, payload: false });
        }

        // Dispatch custom event to trigger scroll restoration after products are loaded
        window.dispatchEvent(new CustomEvent('collection-products-loaded', {
          detail: {
            type: 'plp',
            productsCount: incomingProducts.length,
            totalResults: event.detail.totalResults,
            page: event.detail.page
          }
        }));
      }
    };
    // Loading state handlers
    const handleShowSpinner = (event) => {
      if (event.detail.id === 'spinner-collection' || event.detail.id === 'spinner-search') {
        dispatch({ type: actionTypes.SET_LOADING, payload: true });
      }
    };

    const handleHideSpinner = (event) => {
      if (event.detail.id === 'spinner-collection' || event.detail.id === 'spinner-search') {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    };

    // Set up event listeners
    window.addEventListener('product-list', handleProductsResults);
    window.addEventListener('searchrequest-products-complete', handleProductsResults);
    window.addEventListener('filter-list', handleFiltersResults);
    window.addEventListener('show-spinner', handleShowSpinner);
    window.addEventListener('hide-spinner', handleHideSpinner);

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener('product-list', handleProductsResults);
      window.removeEventListener('searchrequest-products-complete', handleProductsResults);
      window.removeEventListener('filter-list', handleFiltersResults);
      window.removeEventListener('show-spinner', handleShowSpinner);
      window.removeEventListener('hide-spinner', handleHideSpinner);
    };
  }, []);

  // Debug: Log state changes
  useEffect(() => {}, [state.currentPage, state.totalResults, state.products?.length, state.loading]);

  // Load all pages up to a specific page number (for URL restoration)
  const loadAllPagesUpTo = async (targetPage) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: true });

    // We'll load all pages sequentially to build up the product list
    for (let page = 1; page <= targetPage; page++) {
      // Dispatch event to load this page
      await new Promise((resolve) => {
        const loadHandler = (event) => {
          if (event.detail.type === 'plp' && event.detail.page === page) {
            window.removeEventListener('product-list', loadHandler);
            resolve();
          }
        };

        window.addEventListener('product-list', loadHandler);

        window.dispatchEvent(
          new CustomEvent('searchrequest', {
            detail: {
              type: 'plp',
              collection: defaultSettings.collection?.handle,
              page: page,
              productsPerPage: state.productsPerPage,
              skipUrlUpdate: page < targetPage // Only update URL on the final page
            }
          })
        );
      });
    }

    // Update the current page in state
    dispatch({ type: actionTypes.SET_CURRENT_PAGE, payload: targetPage });
  };

  // Load more products (pagination)
  const loadMoreProducts = useCallback(() => {
    if (state.loading || state.isLastPage) return;

    const nextPage = state.currentPage + 1;
    dispatch({ type: actionTypes.SET_CURRENT_PAGE, payload: nextPage });
    dispatch({ type: actionTypes.SET_LOADING, payload: true });

    // Dispatch searchrequest event for pagination
    window.dispatchEvent(
      new CustomEvent('searchrequest', {
        detail: {
          type: 'plp',
          collection: defaultSettings.collection?.handle,
          page: nextPage,
          productsPerPage: state.productsPerPage,
          paginationType: 'load_more'
        }
      })
    );
  }, [state.loading, state.isLastPage, state.currentPage, state.productsPerPage, defaultSettings.collection?.handle]);

  // Apply filters
  const applyFilters = useCallback((filterObject, contextType = 'plp') => {
    dispatch({ type: actionTypes.SET_ACTIVE_FILTERS, payload: filterObject });
    dispatch({ type: actionTypes.SET_LOADING, payload: true });

    // Reset to first page when filtering
    dispatch({ type: actionTypes.SET_CURRENT_PAGE, payload: 1 });

    // Dispatch filter event based on context type
    const filterEvent = new CustomEvent('searchrequest-filter-applied', {
      detail: {
        filters_object: filterObject,
        type: contextType
      }
    });

    window.dispatchEvent(filterEvent);
  }, []);

  // Change sort option
  const changeSortOption = useCallback((option, contextType = 'plp') => {
    dispatch({ type: actionTypes.SET_SORT_OPTION, payload: option });
    dispatch({ type: actionTypes.SET_LOADING, payload: true });
    dispatch({ type: actionTypes.SET_IS_SORTING, payload: true });

    // Dispatch sort event based on context type
    const sortEvent = new CustomEvent('searchrequest-sortchanged', {
      detail: {
        sortOption: option,
        type: contextType
      }
    });
    window.dispatchEvent(sortEvent);
  }, []);
  // Memoize context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(() => ({
    ...state,
    loadMoreProducts,
    applyFilters,
    changeSortOption,
    dispatch,
    actionTypes
  }), [
    state.products,
    state.filters, 
    state.activeFilters,
    state.sortOption,
    state.loading,
    state.initialLoadComplete,
    state.totalResults,
    state.currentPage,
    state.isLastPage,
    state.productsPerPage,
    state.isSorting,
    loadMoreProducts,
    applyFilters,
    changeSortOption
  ]);
  return (
    <CollectionContext.Provider value={contextValue}>
      {children}
    </CollectionContext.Provider>
  );
};

// Custom hook for using the collection context
export const useCollection = () => {
  const context = useContext(CollectionContext);
  if (!context) {
    throw new Error('useCollection must be used within a CollectionProvider');
  }
  return context;
};

// Selector hook for just products
export const useProducts = () => {
  const context = useContext(CollectionContext);
  if (!context) {
    throw new Error('useProducts must be used within a CollectionProvider');
  }
  return context.products;
};

export default CollectionContext;
